import { ReactNode } from 'react';
import { useState, useRef, useCallback } from 'react';

import { useSearch } from '@/hooks/useSearch';
import { useActiveIndex } from './useActiveIndex';

import { SearchInput } from './SearchInput';

import { focusAndOpenKeyboard } from '@/utilities/focusAndOpenKeyboard';

type Data = ReturnType<typeof useSearch>['data'];

type RenderParams = {
  data: Data;
  query: string;
  activeIndex: number;
  reset: () => void;
  focus: () => void;
};

interface Props {
  onEnterKeyDown?: (result: Data[number]) => void;
  children?: (props: RenderParams) => ReactNode;
}

export const Search = ({ onEnterKeyDown, children }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  const { data, status } = useSearch(query);

  const { index, indexUp, indexDown, setIndex } = useActiveIndex(
    data.length - 1
  );

  const handleEnter = useCallback(() => {
    onEnterKeyDown?.(data[index]);
    setIndex(-1);
    setQuery('');
  }, [data, index, setIndex, onEnterKeyDown]);

  return (
    <>
      <SearchInput
        ref={inputRef}
        value={query}
        status={status}
        onChange={setQuery}
        onArrowDown={indexDown}
        onArrowUp={indexUp}
        onEnter={handleEnter}
      />
      {children?.({
        data,
        query,
        activeIndex: index,
        reset: () => setQuery(''),
        focus: () => focusAndOpenKeyboard(inputRef.current, 300),
      })}
    </>
  );
};
